import { useState, useEffect } from 'react';
import type { ClinicFormData, Specialization } from '../../types/signup';
import { ProgressIndicator } from './ProgressIndicator';

interface ClinicCreationProps {
  onSubmit: (data: ClinicFormData) => void;
  loading: boolean;
  error: string | null;
  initialData?: ClinicFormData | null;
  onNameChange?: (name: string) => void;
  onSpecializationChange?: (specializationId: number) => void;
  specializations: Specialization[];
}

export const ClinicCreation = ({
  onSubmit,
  loading,
  error,
  initialData,
  onNameChange,
  onSpecializationChange,
  specializations,
}: ClinicCreationProps) => {
  const [clinicName, setClinicName] = useState(initialData?.name || '');
  const [selectedSpecializationId, setSelectedSpecializationId] = useState<number | undefined>(
    initialData?.specialization_id
  );

  // Set first specialization as default once the list is loaded
  useEffect(() => {
    if (!selectedSpecializationId && specializations.length > 0) {
      const defaultId = specializations[0].id;
      setSelectedSpecializationId(defaultId);
      onSpecializationChange?.(defaultId);
    }
  }, [specializations, selectedSpecializationId, onSpecializationChange]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setClinicName(newName);
    onNameChange?.(newName);
  };

  const handleSpecializationChange = (id: number) => {
    setSelectedSpecializationId(id);
    onSpecializationChange?.(id);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: clinicName,
      specialization_id: selectedSpecializationId,
    });
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto no-scrollbar p-8 md:p-16 flex flex-col justify-center">
        <ProgressIndicator currentStep={1} />

        <form onSubmit={handleSubmit} className="space-y-12">
          {/* Clinic Name Section */}
          <div className="space-y-6">
            <label 
              className="block text-3xl md:text-4xl font-extrabold text-[#101822] tracking-tight leading-tight text-balance" 
              htmlFor="clinicName"
            >
              ¿Cuál es el nombre de tu clínica?
            </label>
            
            <div className="relative group">
              <input
                className="w-full bg-white border-2 border-[#101822]/5 rounded-2xl px-6 py-5 text-xl md:text-2xl text-[#101822] placeholder-[#101822]/20 focus:border-primary focus:ring-4 focus:ring-primary/5 focus:bg-white transition-all duration-300 outline-none shadow-sm"
                id="clinicName"
                placeholder="ej. Centro de Visión Plus"
                type="text"
                value={clinicName}
                onChange={handleNameChange}
                required
              />
              {clinicName && (
                <div className="absolute right-6 top-1/2 -translate-y-1/2 text-primary">
                  <span className="material-icons-round text-3xl">check_circle</span>
                </div>
              )}
            </div>

            <p className="text-[#101822]/40 text-sm flex items-center gap-2 font-medium">
              <span className="material-symbols-outlined text-primary/60 text-xl">info</span>
              Este nombre aparecerá en la documentación clínica.
            </p>
          </div>

          {/* Specialization Section */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-[#101822]/80">Selecciona la especialización de la clínica</h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {specializations.map((spec) => (
                <button
                  key={spec.id}
                  type="button"
                  onClick={() => handleSpecializationChange(spec.id)}
                  className={`group relative flex flex-col items-center justify-center gap-3 p-4 sm:p-6 rounded-2xl transition-all duration-300 active:scale-95 ${
                    selectedSpecializationId === spec.id
                      ? 'bg-white border-2 border-primary shadow-[0_20px_40px_rgba(43,124,238,0.12)]'
                      : 'bg-white/40 border-2 border-transparent hover:border-[#101822]/10 hover:bg-white'
                  }`}
                >
                  <div
                    className={`relative z-10 w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                      selectedSpecializationId === spec.id
                        ? 'bg-primary/10 group-hover:bg-primary'
                        : 'bg-[#101822]/5 group-hover:bg-[#101822]/10'
                    }`}
                  >
                    <span
                      className={`material-icons-round text-2xl transition-colors ${
                        selectedSpecializationId === spec.id
                          ? 'text-primary group-hover:text-white'
                          : 'text-[#101822]/40'
                      }`}
                    >
                      {spec.icon}
                    </span>
                  </div>
                  <span
                    className={`relative z-10 font-bold text-sm tracking-wide transition-colors ${
                      selectedSpecializationId === spec.id
                        ? 'text-primary'
                        : 'text-[#101822]/50 group-hover:text-[#101822]'
                    }`}
                  >
                    {spec.name}
                  </span>
                  {selectedSpecializationId === spec.id && (
                    <div className="absolute top-3 right-3 w-2 h-2 bg-primary rounded-full ring-4 ring-primary/20"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}
        </form>
      </div>

      {/* Bottom Action Button */}
      <div className="p-8 md:p-12 border-t border-[#101822]/5 bg-white/60 backdrop-blur-xl">
        <button
          onClick={handleSubmit}
          disabled={loading || !clinicName.trim()}
          className="w-full group relative overflow-hidden rounded-2xl !bg-primary !p-5 shadow-xl shadow-primary/20 transition-all duration-300 hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          <div className="relative flex items-center justify-center gap-3">
            <span className="font-bold text-white text-lg tracking-wide">
              {loading ? 'Creando clínica...' : 'Crear clínica'}
            </span>
            {!loading && (
              <span className="material-icons-round text-white transition-transform group-hover:translate-x-1">
                arrow_forward
              </span>
            )}
          </div>
        </button>
      </div>
    </>
  );
};
